import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react'
import { savedFilterListApi, csfListApi, DeleteSavedFilterListApi, EditSavedFilterListApi, mailMeGroupListApi } from '../../Config/config';
import axios from 'axios';
import { moduleValues, primaryMasterSubValues, primaryMasterValues } from '../../Constants/values';
import { useFilters, usePagination, useTable } from 'react-table';
import DashboardPagination from '../shared/DashboardPagination';
import OverflowToolTip from '../shared/OverflowToolTip';
import { FaExternalLinkAlt, FaEdit, FaTrash } from 'react-icons/fa';
import { FiEdit2, FiTrash2, FiMail, FiArrowUpRight } from 'react-icons/fi';
import { TbCoins, TbCoinRupee } from 'react-icons/tb';
import { Tooltip, Modal, Input } from 'antd';
import { NavLink, useNavigate } from 'react-router-dom';
import routePathSegments, { getRoutePathById } from '../../routeSegments';
import Loader from '../Loader';
import NoDataPlaceholder from '../shared/NoDataPlaceholder';
import { UserContext } from '../../Contexts/UserContext';
import moment from "moment";
import Constants from '../../Constants';
import DashBoardTableTwo, { getPath } from './DashBoardTableTwo';
import VIUpdates, { hasVIUpdatesAccess } from './VIUpdates';
import CsfPageMock from './CsfPageMock';
import useModule from '../../hooks/useModule';
import { nanoid } from 'nanoid';
import { toast } from 'react-toastify';
/**
 * The function `getSectionLabelById` takes an ID as input and returns the corresponding label for that
 * ID, based on predefined values and constants.
 * @returns The function `getSectionLabelById` returns the label of a section based on its ID.
 */
const getSectionLabelById = (id) => {
    if(id == null){
        return null
    }
    const primaryMasterSub = primaryMasterSubValues.find(option=>parseInt(option.value) == parseInt(id))
    if(primaryMasterSub){
        return primaryMasterSub.title;
    }

    const key = Object.keys(Constants.primaryMasterSubIds)
        .find(subIdKey => Constants.primaryMasterSubIds[subIdKey] == id) ?? ""

    const label = key.split("_").filter(value => value != "id")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    return label;
}

/* Amount sought comes back either already in Cr or in absolute rupees. */
const formatFundingSought = (value) => {
    if (!value || value <= 0) {
        return "--";
    }
    return `₹${value > 10000000 ? (value / 10000000).toFixed(2) : value} Cr`;
};

const formatRevenue = (value) => (value && value >= 0.01 ? `₹${value} Cr` : "--");

/* First letters of the first two words of the company name, used as the card avatar. */
const getCompanyInitials = (name) => (name ?? "")
    .split(" ")
    .filter(word => word.length > 0)
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join("");

/* One of the two panels the 'Companies Seeking Funding' section is split into. */
const CsfPanel = ({ title, action, footer, children }) => (
    <div className="flex flex-col min-h-0 border border-[#CECECE] rounded-[6px] bg-white overflow-hidden">
        <div className="flex items-center justify-between bg-[#c3c3c3] px-4 h-[2.3rem] flex-shrink-0">
            <h3 className="font-sans_medium_body text-sm leading-[16px] text-[#273746]">{title}</h3>
            {action}
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
            {children}
        </div>
        {footer}
    </div>
);

/* A single company seeking funding, rendered as a card inside the Investors panel. */
const CsfCompanyCard = ({ company }) => (
    <div
        className="group flex flex-col rounded-[10px] border-2 border-[#E0E0E0] bg-white p-3 shadow-[0_2px_6px_rgba(0,0,0,0.10)] hover:border-[#BC8B20] hover:shadow-[0_6px_16px_rgba(0,0,0,0.16)] transition-all cursor-pointer"
        onClick={() => window.location.href = `/csf/company/${company.fund_req_id}`}
    >
        <div className="flex items-start space-x-2">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#FDF7E9] border border-[#EADCB4] text-[12px] leading-[12px] font-sans_medium_body text-[#BC8B20]">
                {getCompanyInitials(company.company)}
            </div>
            <div className="min-w-0 flex-1">
                <OverflowToolTip
                    className="w-full"
                    component={
                        <span className="font-sans_medium_body text-[14px] leading-[17px] text-[#333333] group-hover:text-[#BC8B20]">
                            {company.company}
                        </span>
                    }
                />
                <OverflowToolTip
                    className="w-full -mt-[3px]"
                    component={
                        <span className="font-sans_book_body text-[12px] leading-[15px] text-[#8A8A8A]">
                            {company.industry ? company.industry : "--"}
                        </span>
                    }
                />
            </div>
        </div>
        <div className="mt-auto space-y-1.5 border-t border-[#F0F0F0] pt-2">
            <div className="flex items-center justify-between space-x-2">
                <span className="flex items-center space-x-1.5 whitespace-nowrap font-sans_book_body text-[11px] leading-[14px] text-[#9A9A9A]">
                    <TbCoins className="flex-shrink-0 text-[#BC8B20]" size={13} />
                    <span>Funding Sought</span>
                </span>
                <span className="whitespace-nowrap font-sans_medium_body text-[13px] leading-[16px] text-[#333333]">
                    {formatFundingSought(company.fundingSought)}
                </span>
            </div>
            <div className="flex items-center justify-between space-x-2">
                <span className="flex items-center space-x-1.5 whitespace-nowrap font-sans_book_body text-[11px] leading-[14px] text-[#9A9A9A]">
                    <TbCoinRupee className="flex-shrink-0 text-[#BC8B20]" size={14} />
                    <span>Revenue</span>
                </span>
                <span className="whitespace-nowrap font-sans_medium_body text-[13px] leading-[16px] text-[#333333]">
                    {formatRevenue(company.revenue)}
                </span>
            </div>
        </div>
    </div>
);

/* The above code is a React component called `FilterListDataTable`. It is responsible for rendering a
table that displays a list of saved filters. The component uses various hooks such as `useState`,
`useEffect`, and `useContext` to manage state and perform side effects. */
function FilterListDataTable() {
    const [savedFilterList, setSavedFilterList] = useState([]);
    const [combinedFilterList, setCombinedFilterList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // const { applyFilterFrom } = useContext(PeFilterContext);
    const [selectedPrimaryModule, setselectedPrimaryModule] = useState('All');
    const {allowedModulesList} = useModule()
    const hasViUpdatesAccess = hasVIUpdatesAccess();
    const [selectedCategory, setSelectedCategory] = useState(hasViUpdatesAccess ? "VI Updates" : "Companies Seeking Funding")
    const {getToken, getUser} = useContext(UserContext);
    const navigate = useNavigate();
    const [savedGroupList,setSavedGroupList] =useState([])
    const [moduleOptions,setModuleOptions] = useState([])
    const [fundingData, setFundingData] = useState([]);
    const [fundingTotalRecords, setFundingTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoadingFunding, setIsLoadingFunding] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editingFilter, setEditingFilter] = useState(null);
    const [newFilterName, setNewFilterName] = useState('');
    const [mailLoadingId, setMailLoadingId] = useState(null);
    const itemsPerPage = 6;
    const [reachedLimitPopup, setReachedLimitPopup] = useState({ show: false, message: "" });

    // const getSavedList = () => {
    //     setIsLoading(true);
    //     axios.post(savedFilterListApi, {
    //         "user_id": getUser().id,
    //         // "module_id": 1,
    //         "primary_type_master_id": selectedPrimaryModule,
    //         "filter_type": selectedCategory,
    //         // "sub_type_master_id": []
    //     },{
    //         headers:{
    //             "Authorization": getToken()
    //         }
    //     }).then(response => {
    //         setIsLoading(false);
    //         if (response.status === 200) {
    //             setSavedFilterList(response.data?.listviewfilter ?? [])
    //             setSavedGroupList(response?.data?.groupList ?? [])
    //         }
    //     })
    // }
    // console.log(savedGroupList,'savedGroupList');

    useEffect(() => {
        setIsLoading(true);

        if(selectedCategory !== 'Companies Seeking Funding' && selectedCategory !== 'VI Updates'){
            axios.post(savedFilterListApi, {
                "user_id": getUser().id,
                "primary_type_master_id": selectedPrimaryModule,
                "filter_type": selectedCategory,
            }, {
                headers: {
                    "Authorization": getToken()
                }
            }).then(response => {
                setIsLoading(false);
                if (response.status === 200) {
                    if (selectedCategory === 'My Filters') {
                        setSavedFilterList(response.data?.listviewfilter ?? []);
                    } else {
                        const listViewFilters = response.data?.listviewfilter ?? [];
                        const groupViewFilters = response.data?.grpviewfilter ?? [];
                        const combined = [...listViewFilters, ...groupViewFilters.map(item => ({...item, isGroupView: true}))];
                        setCombinedFilterList(combined);
                    }
                    setSavedGroupList(response?.data?.groupList ?? [])
                }
            });
        }
    }, [selectedCategory, selectedPrimaryModule]);  

    // useEffect(()=>{
    //     (async () => {
    //         const payload = {
    //             per_page: 10,
    //             order_by: {
    //                 sort_by: "industry",
    //                 order: "desc"
    //             }
    //         };
    
    //         try {
    //             const response = await fetch(csfListApi, {
    //                 method: 'POST',
    //                 headers: {
    //                     'Content-Type': 'application/json',
    //                     'Authorization': getToken()
    //                 },
    //                 body: JSON.stringify(payload)
    //             });
    
    //             if (!response.ok) {
    //                 throw new Error(`HTTP error! Status: ${response.status}`);
    //             }
    
    //             const data = await response.json();
    //             console.log(data.data, "csf");
    
    //             setFundingData(data?.data?.map(item => ({
    //                 company: item.registered_name,
    //                 industry: item.industry,
    //                 fundingSought: item.amount_sought,
    //                 revenue: item.revenue,
    //                 pitchDeck: item.pitch_doc_url
    //             })));
    
    //         } catch (error) {
    //             console.error('Error fetching data:', error);
    //         }
    //     })(); 
    // },[])


    const fetchFundingData = async (page = 1) => {
        setIsLoadingFunding(true);
        const payload = {
            page: page,
            per_page: itemsPerPage,
            order_by: {
                sort_by: "created_at",
                order: "desc"
            }
        };
    
        try {
            const response = await fetch(csfListApi, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': getToken()
                },
                body: JSON.stringify(payload)
            });
    
                if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
    
            const data = await response.json();
    
            setFundingData(data?.data?.map(item => ({
                company: item.registered_name,
                industry: item.industry,
                fundingSought: item.amount_sought,
                revenue: item.revenue,
                fund_req_id:item.fund_req_id,
                pitchDeck: item.pitch_doc_url
            })) || []);
            setFundingTotalRecords(data?.total || 0);
        } catch (error) {
            console.error('Error fetching funding data:', error);
            // toast.error('Failed to fetch funding data');
        } finally {
            setIsLoadingFunding(false);
        }
    };

    // Call fetchFundingData when the category changes to 'Companies Seeking Funding'
    useEffect(() => {
        if (selectedCategory === 'Companies Seeking Funding') {
            fetchFundingData(currentPage);
        }
    }, [selectedCategory, currentPage]);

    // Handle pagination change for funding data
    const onFundingPaginationChange = (page) => {
        setCurrentPage(page);
    };

    const mailme = async (group_id) => {
        setMailLoadingId(group_id);
        try {
            const response = await axios.post(mailMeGroupListApi, {
                filter_id: group_id,
                savedType: selectedCategory,
                mailMe: true
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": getToken()
                }
            });

            if(response?.message?.includes("maximum limit")) {
                setReachedLimitPopup({ show: true, message: response })
            }

            if (response?.data?.status) {
                toast.success('Mail export has been initiated successfully.');
                
                // fetchWishList();
            }
        } catch (error) {
            if (error?.response?.status === 400) {
                const errorMessage = error?.response?.data?.message || 'An error occurred';
                setReachedLimitPopup({show: true, message: errorMessage });
            } else {
                console.error('Mail Me API error:', error);
                toast.error(error.message);
            }
        } finally {
            setMailLoadingId(null);
        }
    }


    const generateId = ()=>nanoid()
    const queryId = generateId()

   /* The above code is defining a function called `onViewClick` in JavaScript React. This function
   takes an `id` parameter and is defined using the `useCallback` hook. */
   const onViewClick = useCallback(async (id, isGroupView) => {
    const clickedFilter = selectedCategory === 'My Filters' ? savedFilterList.find(filter => filter.id === id) : combinedFilterList.find(filter => filter.id === id);
    if (!clickedFilter) {
        toast.error('Filter not found');
        return;
    }
    if (isGroupView) {
        navigate(getPath(clickedFilter),{state:{myList:{myList:[{id:0,group_id:clickedFilter?.id,name:clickedFilter?.group_name,type:clickedFilter?.type_name}]},monthRange:['1998-01-01', moment().endOf("month").format("YYYY-MM-DD")]}})
    } else {
        // Existing logic for list view filters
        const moduleId = parseInt(clickedFilter.module_id);
        const primaryId = parseInt(clickedFilter.primary_type_master_id);
        const subPrimaryId = parseInt(clickedFilter.sub_type_master_id);
        const request_json = JSON.parse(clickedFilter?.request_json);

        // Filters saved in betweeen of these dates doesn't have the filters saved in db because of implemented common filters and 
        // filtes not send when saving but the api's payload was saved in the currentFilters state and stored in the db.
        // so created a @legacyFilterData in the state holds the payload
        const filterCreatedDate = moment(clickedFilter.created_at).format("YYYY-MM-DD");
        const isCreatedDateInRange = moment(filterCreatedDate).isBetween("2025-04-21", "2025-09-21", "day", "[]");
        console.log(isCreatedDateInRange, "date")

        if(request_json.advance_search){
            localStorage.setItem(queryId, JSON.stringify(request_json?.advance_search));
            if(moduleId === 1){
                navigate(`/${routePathSegments.pe}/${routePathSegments.query_builder}/${routePathSegments.companies}?id=${queryId}`)
            } else if(moduleId === 2){
                navigate(`/${routePathSegments.pe}/${routePathSegments.query_builder}/${routePathSegments.investors}?id=${queryId}`)
            }else if (moduleId === 3){
                navigate(`/${routePathSegments.pe}/${routePathSegments.query_builder}/${routePathSegments.advisors}?id=${queryId}`)
            } else navigate(`/${routePathSegments.pe}/${routePathSegments.query_builder}/${routePathSegments.transaction_advisors}?id=${queryId}`)
        }  else {

            const filterPayload = JSON.parse(clickedFilter?.request_json) ?? {};
            const dateRange = filterPayload?.date_range;

            console.log("clickedFilter", filterPayload);

            // helper: check if filterData has any values (ignoring globalSearch + date_range)
            function hasFilterValues(filterData) {
                if (!filterData || typeof filterData !== "object") return false;

                return Object.entries(filterData).some(([key, value]) => {
                    if (key === "globalSearch" || key === "date_range" || key === "deals_operator") return false;
                    if (Array.isArray(value)) return value.length > 0;
                    if (typeof value === "object" && value !== null)
                    return Object.values(value).some((v) => {
                        if (Array.isArray(v)) return v.length > 0;
                        if (typeof v === "object" && v !== null)
                        return Object.values(v).some((inner) =>
                            Array.isArray(inner) ? inner.length > 0 : !!inner
                        );
                        return !!v;
                    });
                    return !!value;
                });
            }

            // default state
            const state = {
                monthRange: [dateRange?.from_date, dateRange?.to_date],
                search_name: clickedFilter.name,
                created_at: filterCreatedDate,
                isCreatedDateInRange,
            };

            if (isCreatedDateInRange) {
                if (hasFilterValues(filterPayload.filterData)) {
                    // keep filterData as is
                    state.filterData = filterPayload.filterData;
                } else {
                    // move everything into legacyFilterData
                    state.legacyFilterData = { ...filterPayload };
                    delete state.legacyFilterData.filterData;
                }
            } else {
                state.filterData = filterPayload?.filterData ?? {};
            }

            if(filterPayload.from === "gs"){
                state.gsResults = { ...filterPayload }
                delete state.gsResults.filterData
                delete state.legacyFilterData
            }

            console.log("Navigating with state:", state);

            navigate(getRoutePathById({ moduleId, primaryId, subPrimaryId }), { state });
        }
    }
}, [selectedCategory, savedFilterList, combinedFilterList, navigate]);
    const handleEdit = (e, filter) => {
        e.stopPropagation();
        setEditingFilter(filter);
        setNewFilterName(filter.name);
        setIsEditModalVisible(true);
    };

    const handleDelete = async (e, filterId, typeId) => {
        e.stopPropagation();
        Modal.confirm({
            title: 'Are you sure you want to delete this list?',
            content: 'This action cannot be undone.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    const response = await axios.post(DeleteSavedFilterListApi, {
                        listviewfilter_id: filterId,
                        type_id: typeId
                    }, {
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": getToken()
                        }
                    });
        
                    if (response.status === 200) {
                        toast.success('Filter deleted successfully');
                        // Refresh the filter list
                        const updatedList = savedFilterList.filter(filter => filter.id !== filterId);
                        setSavedFilterList(updatedList);
                    }
                } catch (error) {
                    // toast.error('Failed to delete filter');
                    console.error('Error deleting filter:', error);
                }
            }
        });
        
    };

    const handleEditSubmit = async () => {
        try {
            const response = await axios.post(EditSavedFilterListApi, {
                listviewfilter_id: editingFilter.id,
                type_id: editingFilter.module_id,
                name: newFilterName
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": getToken()
                }
            });

            if (response.status === 200) {
                toast.success('Filter name updated successfully');
                // Update the filter list
                const updatedList = savedFilterList.map(filter => 
                    filter.id === editingFilter.id ? { ...filter, name: newFilterName } : filter
                );
                setSavedFilterList(updatedList);
                setIsEditModalVisible(false);
            }
        } catch (error) {
            // toast.error('Failed to update filter name');
            console.error('Error updating filter:', error);
        }
    };

    const columns = useMemo(() => {
        const baseColumns = [
            {
                Header: "List Name",
                accessor: "searchName",
                className: "text-left",
                Cell: ({ value, row }) => {
                    // Get the original item directly from the row data
                    const originalItem = row.original.originalItem;
                    
                    return (
                        <div 
                            className={`w-full ${selectedCategory === "My Filters" ? "cursor-pointer hover:text-[#bc8b20] font-semibold" : ""}`}
                            onClick={(e) => {
                                if (selectedCategory === "My Filters") {
                                    e.stopPropagation(); 
                                    onViewClick(originalItem.id, originalItem.isGroupView);
                                }
                            }}
                        >
                            <OverflowToolTip 
                                title={value} 
                                component={
                                    <span className="block w-full truncate">{value}</span>
                                } 
                                className="w-full"
                            />
                        </div>
                    );
                }
            },
            // Other columns remain the same
            {
                Header: "Module",
                accessor: "module",
                className: "text-center",
                Cell: ({ value }) => {
                    return primaryMasterValues.find(data => data.value == value)?.title
                },
            },
            {
                Header: "Type",
                accessor: "type",
                className: "text-center"
            },
            {
                Header: "Created Date",
                accessor: "createdDate",
                className: "text-center"
            }
        ];
        
        // Add Actions column only for My Filters
        if (selectedCategory === 'My Filters') {
            baseColumns.push({
                Header: "Actions",
                accessor: "actions",
                className: "text-center",
                Cell: ({ row }) => (
                    <div className="flex justify-center space-x-3">
                        <FiEdit2
                            className="cursor-pointer text-[#bc8b20] hover:text-[#efce88]"
                            onClick={(e) => handleEdit(e, savedFilterList.find(filter => filter.id === row.original.actions.id))}
                        />
                        <FiTrash2
                            className="cursor-pointer text-[#bc8b20] hover:text-[#efce88]"
                            onClick={(e) => handleDelete(e, row.original.actions.id, row.original.module)}
                        />
                    </div>
                )
            });
        }

        // Add Mail Me column as the last column only for My Filters (Saved Search)
        if (selectedCategory === 'My Filters') {
            baseColumns.push({
                Header: "Mail Me",
                accessor: "mailMe",
                className: "text-center",
                Cell: ({ row }) => {
                    console.log('Row ID:', row.original.actions);
                    const rowId = row.original.actions?.id;
                    const isLoading = mailLoadingId === rowId;
                    
                    const handleMailClick = (e) => {
                        e.stopPropagation(); // Prevent row click event
                        if (!isLoading) {
                            mailme(rowId);
                        }
                    };

                    if(row.original.actions.mailMe){
                        console.log(row.original.actions.mailMe)
                        return (
                        <div className="flex justify-center">
                            <Tooltip title={isLoading ? "Sending..." : "Mail Me"}>
                                {isLoading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#bc8b20]"></div>
                                ) : (
                                    <FiMail 
                                        className="cursor-pointer text-[#bc8b20] hover:text-[#efce88]" 
                                        onClick={handleMailClick}
                                        size={16}
                                    />
                                )}
                            </Tooltip>
                        </div>
                    );
                    }
                }
            });
        }

        return baseColumns;
    }, [selectedCategory, savedFilterList, mailLoadingId]);

    const data = useMemo(() => {
        const filtersToProcess = selectedCategory === 'My Filters' ? savedFilterList : combinedFilterList;
        
        return filtersToProcess.map(filter => {
            if (filter.isGroupView && selectedCategory !== 'My Filters') {
                return {
                    id: filter.id, // Store the original ID directly in the row data
                    module: filter.primary_type_master_id,
                    type: filter.type_name,
                    subType: "--",
                    searchName: filter.name,
                    createdDate: moment(filter.created_at).format('DD MMM YYYY'),
                    actions: { id: filter.id, isGroupView: true },
                    originalItem: filter // Store the entire original item for reference
                };
            } else {
                // List view filter
                const filters = JSON.parse(filter.request_json) ?? {};
                let keys = new Set();
                Object.keys(filters)?.forEach(key => {
                    if (Array.isArray(filters[key])) {
                        if (filters[key].length > 0) {
                            keys.add(key.toUpperCase().replace('_', " "));
                        }
                    } else if (typeof filters[key] === "object") {
                        keys.add(key.toUpperCase().replace('_', " "));
                    }
                });

                return {
                    id: filter.id, // Store the original ID directly in the row data
                    module: filter.primary_type_master_id,
                    type: moduleValues.find(data => data.value == filter.module_id)?.title,
                    subType: getSectionLabelById(filter.sub_type_master_id),
                    searchName: filter.name,
                    appliedFilter: Array.from(keys),
                    createdDate: moment(filter.created_at).format('DD MMM YYYY'),
                    actions: { id: filter.id, isGroupView: false, mailMe: filter.export_json === null ? false : true },
                    originalItem: filter // Store the entire original item for reference
                };
            }
        });
    }, [selectedCategory, savedFilterList, combinedFilterList]);

    const tableInstance = useTable({
        columns,
        data,
        initialState: { pageSize: 10 }
    },
        useFilters,
        usePagination
    )

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        // pageCount,
        gotoPage,
        // nextPage,
        // previousPage,
        setFilter,
        state: { pageIndex, pageSize },
    } = tableInstance

   /**
    * The function `onPaginationChange` is used to handle pagination changes in a React component.
    */
    const onPaginationChange = (page, pageSize) => {
        gotoPage(page - 1)
    }

    /**
     * The function `onModuleChange` updates the value of `selectedPrimaryModule` based on the value of
     * the target element in an event.
     */
    const onModuleChange = (e) => {
        setselectedPrimaryModule(e.target.value);
    }

    /**
     * The function `onCategoryChange` updates the selected category based on the value passed as an
     * argument.
     */
    const onCategoryChange = (value)=>{
        // alert(value)
        setSelectedCategory(value)
    }

    useEffect(() => {
        setFilter('module', selectedPrimaryModule === "All" ? "" : selectedPrimaryModule)
    }, [selectedPrimaryModule])

    // const allowedModulesStr = localStorage.getItem(Constants.localStorageKeys.allowedModules);
    var allowedModulesData =[] 

    useEffect (()=>{
        
        /* The above code is checking if certain modules are included in the `allowedModulesList`
        array. If a module is included, it adds an object with a `title` and `value` property to the
        `allowedModulesData` array. After checking all the modules, if there is more than one module
        in the `allowedModulesData` array, it adds an additional object with a `title` of "All" and
        a `value` of "All" at the beginning of the array. Finally, it sets the state of
        `moduleOptions` to the `allowedModulesData` array. */
        if(allowedModulesList.includes('pe')){
            allowedModulesData.push({title: "PE", value: "1"},)
        }
        if(allowedModulesList.includes('vc')){
            allowedModulesData.push({title: "VC", value: "2"},)
        }
        if(allowedModulesList.includes('re')){
            allowedModulesData.push({title: "RE", value: "3"},)
        }
        if(allowedModulesList.includes('ma')){
            allowedModulesData.push({title: "M & A", value: "4"},)
        }
        if(allowedModulesList.includes('cfs')){
            allowedModulesData.push({title: "CFS", value: "5"},)
        }
        if(allowedModulesData.length > 1 ){
            allowedModulesData.splice(0,0,{title: "All", value: "All"},)
        }
        setModuleOptions(allowedModulesData)

    },[]) 
    


    if(reachedLimitPopup.show){
        return (
            <div className='flex font-sans_book_body cursor-default justify-center items-center z-10 fixed left-0 top-0 w-screen h-screen  bg-gray-600 bg-opacity-75 transition-opacity'>
                <div className='relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 mx-4 md:mx-0 w-[50%]'>
                    <div className='w-full flex flex-col '>
                        <div className="mt-3 text-center sm:mt-0 px-5 py-3 sm:ml-4 sm:text-left">
                            <h1 className='text-left py-1 text-lg font-bold leading-6 text-gray-900'>Alert!</h1>
                            <div className="mt-4 ">
                                <p className="text-sm text-gray-500 text-start">{reachedLimitPopup.message}</p>
                            </div>
                        </div>
                    </div>
                    <div className='flex bg-gray-50 px-4 pb-3 flex-row-reverse'>
                        <button onClick={() => setReachedLimitPopup(prev => ({ ...prev, show: false }))} className='inline-flex w-full justify-center font-sans_bold_body rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none  sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        )
    }


    return (        
        <>
            <div className="border border-[#CECECE] rounded-[4px] lg:p-2 lg:py-3 bg-white xl:w-[70%]  xl:h-[37rem] flex flex-col">
                <div className="sm:flex flex justify-between items-center px-2 lg:py-2 h-[4rem] lg:h-[2rem] flex-shrink-0">
                    <div className="flex space-x-3 items-center">
                        <div className='pl-4 flex space-x-5 lg:space-x-12 items-center'>
                            {hasViUpdatesAccess &&
                                <p className={`text-[#333333] font-sans_medium_body cursor-pointer ${selectedCategory==='VI Updates' ? 'font-semibold text-[18px] leading-[19px]':'text-[16px] leading-[17px]'}`} onClick={()=>onCategoryChange('VI Updates')}>VI Updates</p>
                            }
                            <p 
                                    className={`text-[#333333] font-sans_medium_body cursor-pointer flex items-center space-x-2 ${selectedCategory==='Companies Seeking Funding' ? 'font-semibold  text-[15px] pb-1 lg:pb-0 lg:text-[18px] leading-[19px]':'text-[16px] leading-[17px]'}`} 
                                    onClick={()=>onCategoryChange('Companies Seeking Funding')}
                                >
                                    <span>Companies Seeking Funding</span>
                                    <NavLink to={routePathSegments.csf} target="_blank">

                                        <FaExternalLinkAlt 
                                            className="cursor-pointer text-[#d6b15f] hover:text-[#bc8b20]"
                                            size={14}
                                        />
                                        </NavLink>
                            </p>                         
                            <p className={`text-[#333333] font-sans_medium_body cursor-pointer ${selectedCategory==='VI Filters' ? 'font-semibold text-[18px] leading-[19px]':'text-[16px] leading-[17px]'}`} onClick={()=>onCategoryChange('VI Filters')}>VI Filters</p>
                            <p className={`text-[#333333] font-sans_medium_body cursor-pointer ${selectedCategory==='My List' ? 'font-semibold text-[18px] leading-[19px]':'text-[16px] leading-[17px]'}`} onClick={()=>onCategoryChange('My List')}>My List</p>                        
                            <p className={`text-[#333333] font-sans_medium_body cursor-pointer ${selectedCategory==='My Filters' ? 'font-semibold  text-[15px] pb-1 lg:pb-0 lg:text-[18px] leading-[19px]':'text-[16px] leading-[17px]'}`} onClick={()=>onCategoryChange('My Filters')}>Saved Search</p>   
                        </div>                     
                    </div> 
                    {moduleOptions.length > 0 && selectedCategory !== 'Companies Seeking Funding' && selectedCategory !== 'VI Updates' &&
                        <select 
                            onChange={onModuleChange}
                            value={selectedPrimaryModule}
                            className="py-0.5 text-[13px] font-sans_book_body text-[#645326] leading-[16px] rounded-[10.5px] block px-2.5 bg-[#FFFFFF] border border-[#BC8B20] focus:outline-none"
                        >
                            {moduleOptions.map((primaryValue,i) => (
                                <option value={primaryValue.value} key={i}>
                                    {primaryValue.title}
                                </option>
                            ))}
                        </select>
                    }
                </div>
                
                <div className='overflow-x-auto lg:thin-scrollbar scrollbar-remove flex-1 min-h-0'>
                    {selectedCategory === 'VI Updates' ? (
                        <VIUpdates />
                    ) :
                      selectedCategory === 'My List' ? (
                        <DashBoardTableTwo savedWishList={savedGroupList} isLoading={isLoading} selectedCategory={selectedCategory} mailme={mailme} mailLoadingId={mailLoadingId}/>
                    ) :
                      selectedCategory === 'Companies Seeking Funding' ? (
                            <div className="h-full px-2 pt-3 pb-2 grid grid-cols-1 lg:grid-cols-2 gap-3">
                                <CsfPanel title="For Advisors">
                                    <div className="flex h-full min-h-0 flex-col px-4 pb-3 pt-7 text-center">
                                        <h4 className="font-sans_medium_body text-[16px] font-semibold leading-[20px] text-[#333333]">
                                            Looking to raise funds for your client?
                                        </h4>
                                        <a
                                            href={`${routePathSegments.compSeekFund}?type=advisor`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="group relative mt-6 flex min-h-0 flex-1 flex-col overflow-hidden rounded-[8px] border-2 border-[#E0E0E0] shadow-[0_2px_6px_rgba(0,0,0,0.10)] hover:border-[#BC8B20] hover:shadow-[0_6px_16px_rgba(0,0,0,0.16)] transition-all"
                                        >
                                            <span className="pointer-events-none absolute top-2 right-2 z-10 flex items-center gap-1 text-[10px] leading-[13px] font-sans_book_body font-semibold text-[#BC8B20] bg-[#FDF7E9] border border-[#EADCB4] rounded-full pl-2.5 pr-2 py-[3px] group-hover:text-white group-hover:bg-[#BC8B20] group-hover:border-[#BC8B20] transition-colors duration-200">
                                                Open form
                                                <FiArrowUpRight size={11} />
                                            </span>
                                            <CsfPageMock />
                                        </a>
                                    </div>
                                </CsfPanel>
                                <CsfPanel
                                    title="For Investors"
                                    footer={
                                        fundingData.length > 0 && !isLoadingFunding &&
                                        <div className="text-center py-2 flex-shrink-0 border-t border-[#F0F0F0]">
                                            <DashboardPagination
                                                current={currentPage}
                                                total={fundingTotalRecords}
                                                pageSize={itemsPerPage}
                                                onChange={onFundingPaginationChange}
                                            />
                                        </div>
                                    }
                                >
                                    {isLoadingFunding ? (
                                        <div className="bg-white w-full flex items-center justify-center h-full">
                                            <Loader />
                                        </div>
                                    ) : fundingData.length === 0 ? (
                                        <div className="bg-white w-full flex items-center justify-center h-full">
                                            <NoDataPlaceholder showButton={false} />
                                        </div>
                                    ) : (
                                        <div className="grid h-full grid-cols-2 grid-rows-3 gap-3 p-3">
                                            {fundingData.map((company, index) => (
                                                <CsfCompanyCard key={company.fund_req_id ?? index} company={company} />
                                            ))}
                                        </div>
                                    )}
                                </CsfPanel>
                            </div>
                     ):(
                        <div className="overflow-x-auto lg:thin-scrollbar scrollbar-remove">                  
                            <table className="w-full h-full p-1 rounded-[4px] border-separate text-center border-spacing-y-1" {...getTableProps()}>
                                <thead className="bg-[#c3c3c3] font-sans_medium_body text-sm leading-[16px] text-[#273746] h-[2.3rem]">
                                    {headerGroups.map(headerGroup => (
                                        <tr {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map(column => (
                                                <th {...column.getHeaderProps({
                                                    className: `whitespace-nowrap px-5 ${column.headerClassName}`
                                                })}>
                                                    {column.render('Header')}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                {!isLoading && (selectedCategory === 'My Filters' ? savedFilterList : combinedFilterList).length > 0 && page.length > 0 ? (
                                    <tbody className="text-[11pt] leading-[12pt] lg:text-[14px] lg:leading-[16px] text-[#333333] font-sans_book_body" {...getTableBodyProps()}>
                                        {page.map(row => {
                                            prepareRow(row);
                                            // Get the original item directly from the row data
                                            const originalItem = row.original.originalItem;
                                            
                                            return (
                                                <tr 
                                                    className={`px-2 even:bg-[#E8E8E8] even:h-[2.3rem] odd:bg-white odd:h-8 ${selectedCategory !== 'My Filters' ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                                                    {...row.getRowProps()} 
                                                    style={{ height: '2.3rem' }}
                                                    onClick={() => {
                                                        if (selectedCategory !== 'My Filters') {
                                                            onViewClick(originalItem.id, originalItem.isGroupView);
                                                        }
                                                    }}
                                                >
                                                    {row.cells.map(cell => (
                                                        <td {...cell.getCellProps({
                                                            className: `lg:whitespace-nowrap px-5 py-2 ${cell.column.className || ''}`
                                                        })}>
                                                            {cell.render('Cell')}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                ) : (
                                    <tbody className='h-[27.5rem]'>
                                        <tr>
                                            <td colSpan={headerGroups[0].headers.length} className=''>
                                                {isLoading ? (
                                                    <div className='bg-white w-full flex items-center justify-center h-full'>
                                                        <Loader />
                                                    </div>
                                                ) : (combinedFilterList.length < 1 || page.length < 1) ? (
                                                    <div className='bg-white w-full flex items-center justify-center h-full'>
                                                        <NoDataPlaceholder showButton={false} />
                                                    </div>
                                                ) : null}
                                            </td>
                                        </tr>
                                    </tbody>
                                )}
                            </table>
                        </div>       
                    )}                    
                </div>                             
                {selectedCategory !== 'VI Updates' && selectedCategory !== 'Companies Seeking Funding' && rows.length > 0 && !isLoading &&
                    <div className='text-center my-2 mt-6'>
                        <DashboardPagination
                            current={pageIndex + 1}
                            total={rows.length}
                            pageSize={pageSize}
                            onChange={onPaginationChange}
                        />
                    </div>
                }  
            </div>                     
            <Modal
                title="Edit Filter Name"
                open={isEditModalVisible}
                onOk={handleEditSubmit}
                onCancel={() => {
                    setIsEditModalVisible(false);
                    setEditingFilter(null);
                    setNewFilterName('');
                }}
                okButtonProps={{ 
                    style: { 
                        backgroundColor: '#bc8b20', 
                        borderColor: '#bc8b20',
                    } 
                }}
                cancelButtonProps={{
                    style: {
                        marginRight: '8px', 
                    }
                }}
                okText="Save"
                cancelText="Cancel"
            >
                <Input
                    value={newFilterName}
                    onChange={(e) => setNewFilterName(e.target.value)}
                    placeholder="Enter new filter name"
                    onPressEnter={handleEditSubmit}
                />
            </Modal>
        </>
    );
}

export default FilterListDataTable